import React from "react";
import { FormSection, InputField } from "../../../common/FormComponents";
import { MenuItem, Select, SelectChangeEvent } from "@mui/material";
import {
  CategoryReturn,
  ChamberMemberCategory,
} from "../../../../graphql/types";
import Tooltip from "../../../common/FormComponents/Tooltip";

type CompanyInfo = {
  membershipType: string;
  fullTimeEmployees: string;
  partTimeEmployees: string;
  companyName: string;
  companyDescription: string;
  companyWebsite: string;
  companyPhone: string;
  companyEmail: string;
  companyCategory: string;
  inBusinessSince: string;
};

type CompanyInfoProps = {
  companyInfo: CompanyInfo;
  onChange: ({
    field,
    value,
  }: {
    field: keyof CompanyInfo;
    value: string;
  }) => void;
  errors?: Record<string, string>;
};

const membershipTypes = [
  { value: "company", label: "Company" },
  { value: "nonProfit", label: "Non-Profit" },
  { value: "independentRetired", label: "Independent/Retired Member" },
  { value: "youngProfessional", label: "Young Professional" },
  { value: "associateCompany", label: "Associate Company" },
];

export const allCategoriesDictionary = [
  { id: ChamberMemberCategory.Restaurant, name: "Food/Drink" },
  { id: ChamberMemberCategory.Retail, name: "Retail" },
  { id: ChamberMemberCategory.PersonalServices, name: "Personal Services" },
  {
    id: ChamberMemberCategory.ProfessionalServices,
    name: "Professional Services",
  },
  {
    id: ChamberMemberCategory.MarketingAdvertising,
    name: "Marketing/Advertising",
  },
  {
    id: ChamberMemberCategory.CommunicationTechnology,
    name: "Communication/Technology",
  },
  {
    id: ChamberMemberCategory.AutomotiveTransportation,
    name: "Automobile/Transportation",
  },
  {
    id: ChamberMemberCategory.RealEstate,
    name: "Real Estate/Development",
  },
  { id: ChamberMemberCategory.FarmingAgriculture, name: "Farming/Agriculture" },
  { id: ChamberMemberCategory.Financial, name: "Financial" },
  {
    id: ChamberMemberCategory.AccommodationsAttractions,
    name: "Accommodations/Attractions",
  },
  {
    id: ChamberMemberCategory.ConstructionIndustrial,
    name: "Construction/Industrial",
  },
  { id: ChamberMemberCategory.NonProfit, name: "Non-profit" },
  {
    id: ChamberMemberCategory.GovernmentEducation,
    name: "Government/Education",
  },
  { id: ChamberMemberCategory.HealthSafety, name: "Health/Safety" },
  { id: ChamberMemberCategory.SportsRecreation, name: "Sports/Recreation" },
] as any as [CategoryReturn];

const CompanyInfoSection = ({
  companyInfo,
  onChange,
  errors = {},
}: CompanyInfoProps) => {
  const handleChange =
    (field: keyof CompanyInfo) => (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ field, value: e.target.value });
    };

  const handleSelectChange =
    (field: keyof CompanyInfo) => (e: SelectChangeEvent<string>) => {
      onChange({ field, value: e.target.value });
    };

  return (
    <FormSection
      title="Company Information"
      tooltipText="Enter the member company's basic information"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
        <div className="mb-6">
          <div className="flex items-center mb-1">
            <label
              htmlFor="membershipType"
              className="block text-sm font-medium text-gray-900"
            >
              Membership Type
              <span className="text-red-500 ml-1">*</span>
            </label>
            <Tooltip content="Select the type of membership for this company" />
          </div>
          <Select
            id="membershipType"
            name="membershipType"
            value={companyInfo.membershipType}
            onChange={handleSelectChange("membershipType")}
            displayEmpty
            fullWidth
            error={!!errors.membershipType}
            sx={{
              backgroundColor: "white",
              borderRadius: 3,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#B0B5BC",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#B0B5BC",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderWidth: 1,
                borderColor: "#E1A731",
              },
              "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                borderColor: "#d32f2f",
              },
              "& .MuiSelect-select": {
                fontSize: 16,
              },
            }}
          >
            <MenuItem value="" disabled>
              Select a membership type
            </MenuItem>
            {membershipTypes.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
          {errors.membershipType && (
            <p className="text-red-500 text-xs mt-1">{errors.membershipType}</p>
          )}
        </div>

        <InputField
          id="company-name"
          name="companyName"
          label="Company Name"
          value={companyInfo.companyName}
          onChange={handleChange("companyName")}
          error={!!errors.companyName}
          helperText={errors.companyName || ""}
          required
          tooltipText="Enter the official name of the company"
        />

        <div className="md:col-span-2">
          <InputField
            id="company-description"
            name="companyDescription"
            label="Company Description"
            value={companyInfo.companyDescription}
            onChange={handleChange("companyDescription")}
            error={!!errors.companyDescription}
            helperText={errors.companyDescription || ""}
            multiline
            rows={3}
            tooltipText="Provide a brief description of the company's products or services"
          />
        </div>

        <InputField
          id="company-website"
          name="companyWebsite"
          label="Company Website"
          value={companyInfo.companyWebsite}
          onChange={handleChange("companyWebsite")}
          error={!!errors.companyWebsite}
          helperText={errors.companyWebsite || ""}
          placeholder="https://www.example.com"
          tooltipText="Enter the company's website URL"
        />

        <InputField
          id="company-email"
          name="companyEmail"
          label="Company Email"
          value={companyInfo.companyEmail}
          onChange={handleChange("companyEmail")}
          error={!!errors.companyEmail}
          helperText={errors.companyEmail || ""}
          required
          tooltipText="Enter the company's general email address"
        />

        <InputField
          id="company-phone"
          name="companyPhone"
          label="Company Phone"
          value={companyInfo.companyPhone}
          onChange={handleChange("companyPhone")}
          error={!!errors.companyPhone}
          helperText={errors.companyPhone || ""}
          tooltipText="Enter the company's main phone number"
        />

        <div className="mb-6">
          <div className="flex items-center mb-1">
            <label
              htmlFor="companyCategory"
              className="block text-sm font-medium text-gray-900"
            >
              Company Category
              <span className="text-red-500 ml-1">*</span>
            </label>
            <Tooltip content="Select the industry category of this company" />
          </div>
          <Select
            id="companyCategory"
            name="companyCategory"
            value={companyInfo.companyCategory}
            onChange={handleSelectChange("companyCategory")}
            displayEmpty
            fullWidth
            error={!!errors.companyCategory}
            sx={{
              backgroundColor: "white",
              borderRadius: 3,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#B0B5BC",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#B0B5BC",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderWidth: 1,
                borderColor: "#E1A731",
              },
              "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                borderColor: "#d32f2f",
              },
              "& .MuiSelect-select": {
                fontSize: 16,
              },
            }}
          >
            <MenuItem value="" disabled>
              Select a company category
            </MenuItem>
            {allCategoriesDictionary.map((category) => (
              <MenuItem key={category.id} value={category.id}>
                {category.name}
              </MenuItem>
            ))}
          </Select>
          {errors.companyCategory && (
            <p className="text-red-500 text-xs mt-1">
              {errors.companyCategory}
            </p>
          )}
        </div>

        <InputField
          id="full-time-employees"
          name="fullTimeEmployees"
          label="Full-Time Employees"
          value={companyInfo.fullTimeEmployees}
          onChange={handleChange("fullTimeEmployees")}
          error={!!errors.fullTimeEmployees}
          helperText={errors.fullTimeEmployees || ""}
          tooltipText="Number of full-time employees at the company"
        />

        <InputField
          id="part-time-employees"
          name="partTimeEmployees"
          label="Part-Time Employees"
          value={companyInfo.partTimeEmployees}
          onChange={handleChange("partTimeEmployees")}
          error={!!errors.partTimeEmployees}
          helperText={errors.partTimeEmployees || ""}
          tooltipText="Number of part-time employees at the company"
        />

        <InputField
          id="in-business-since"
          name="inBusinessSince"
          label="In Business Since"
          value={companyInfo.inBusinessSince}
          onChange={handleChange("inBusinessSince")}
          error={!!errors.inBusinessSince}
          helperText={errors.inBusinessSince || ""}
          placeholder="YYYY"
          tooltipText="Year the company was established"
        />
      </div>
    </FormSection>
  );
};

export default CompanyInfoSection;
